// ── IP 관리 — IP 연결 수정 창 (2026-10-29 사용자 요청) ──────────────────────────
//   IP 주소를 바꾸고(현재 IP 가져오기), 새 IP 의 근무지 이름(별칭)을 정한다 — 기본은 옛 이름을 이어 쓴다.
//   지난 출퇴근 기록은 서버가 건드리지 않는다(api/services/ip-address-change.service.js).
import React, { useEffect, useState } from 'react';
import Modal from '../../../components/Modal';
import { inputStyle, btn, primaryBtn } from './styles';
import { normalizeIp, isLoopbackIp, formatIpWithAlias } from './utils';

const labelStyle = { fontSize: 12, fontWeight: 700, color: 'var(--text-sub)', marginBottom: 6 };
const noteStyle = {
  fontSize: 12, lineHeight: 1.6, color: 'var(--text-muted)', background: 'var(--bg-subtle)',
  border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px',
};

/**
 * Props:
 *   row       — 고칠 IP 연결 행(없으면 닫힘)
 *   currentIp — 지금 이 화면을 연 사람의 IP(현재 IP 가져오기)
 *   aliasByIp — ip → 별칭 Map
 *   busy      — 저장 중
 *   onClose   — 닫기
 *   onSave    — (row, { ip, alias }) => void
 *   showToast — 알림
 */
export function IpEditModal({ row, currentIp, aliasByIp, busy, onClose, onSave, showToast }) {
  const [ip, setIp] = useState('');
  const [alias, setAlias] = useState('');
  const oldIp = row ? normalizeIp(row.ip_address) : '';
  const oldAlias = row ? (aliasByIp.get(oldIp) || row.label || '') : '';

  useEffect(() => {
    if (!row) return;
    setIp(oldIp);
    setAlias(oldAlias);
  }, [row, oldIp, oldAlias]);

  if (!row) return null;

  const fillCurrentIp = () => {
    const c = normalizeIp(currentIp);
    if (!c || c === '-' || isLoopbackIp(c)) {
      showToast('현재 입력 가능한 외부 IP를 찾지 못했습니다.', 'error');
      return;
    }
    setIp(c);
  };

  return (
    <Modal
      open
      icon="🌐"
      title="IP 연결 수정"
      onClose={() => { if (!busy) onClose(); }}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={btn} onClick={onClose} disabled={busy}>취소</button>
          <button style={primaryBtn} onClick={() => onSave(row, { ip, alias })} disabled={busy}>
            {busy ? '저장 중...' : '저장'}
          </button>
        </div>
      )}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ fontSize: 13, color: 'var(--text)' }}>
          <div>연결 대상: <b>{row.user_name ? `${row.user_name} (${row.user_id})` : '공용(모든 직원)'}</b></div>
          <div style={{ marginTop: 4 }}>
            지금 IP: <code style={{ overflowWrap: 'anywhere' }}>{formatIpWithAlias(oldIp, oldAlias)}</code>
          </div>
        </div>

        <div>
          <div style={labelStyle}>새 IP 주소</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...inputStyle, flex: 1 }} value={ip} placeholder="IP 주소"
              onChange={(e) => setIp(e.target.value)} aria-label="새 IP 주소" />
            <button style={{ ...btn, flexShrink: 0 }} onClick={fillCurrentIp} disabled={busy}>현재 IP 가져오기</button>
          </div>
        </div>

        <div>
          <div style={labelStyle}>근무지 이름(별칭)</div>
          <input style={inputStyle} value={alias} placeholder="별칭 없음"
            onChange={(e) => setAlias(e.target.value)} aria-label="근무지 이름" />
        </div>

        <div style={noteStyle}>
          IP를 바꿔도 <b>지난 출퇴근 기록은 그대로</b>예요. 지난 기록은 계속 옛 이름(
          {oldAlias || '별칭 없음'})으로 보여요.<br />
          저장한 순간부터 <b>새 IP에서만</b> 출퇴근돼요. 오늘 옛 IP로 출근만 한 사람이 있으면 저장 전에 한 번 더 물어봐요.
        </div>
      </div>
    </Modal>
  );
}
